/**
 * P2P connection manager wrapping Trystero.
 * Handles room creation/joining, peer lifecycle, and reliable message passing.
 *
 * Tracker strategy:
 * - If a local RELAY_URL is configured (VITE_RELAY_URL for dev/LAN), it's used first.
 * - Public trackers are always included as fallback.
 * - Trystero internally iterates over relay URLs and falls back if one is unreachable.
 */

import { joinRoom, type Room } from "trystero/torrent";
import type { P2PMessage } from "../engine/types";
import { codeToRoomId } from "./protocol";
import type { IP2PConnection, ConnectionState, ConnectionEvents } from "./base";
import { RELAY_URL, RTC_CONFIG, PUBLIC_RELAY_URLS } from "../config/constants";
export type { ConnectionState, ConnectionEvents };

const APP_ID = "pokemon-battle-sim-b2w2";

/**
 * Build the ordered list of relay URLs to pass to Trystero.
 * - If an explicit RELAY_URL is configured (local dev / LAN), it's tried first.
 * - Public trackers are always included so production builds work without env vars.
 */
function buildRelayUrlList(): string[] {
  const urls: string[] = [];
  if (RELAY_URL) {
    urls.push(RELAY_URL);
  }
  urls.push(...PUBLIC_RELAY_URLS);
  return urls;
}

const TRYK_CONFIG = {
  appId: APP_ID,
  relayUrls: buildRelayUrlList(),
  rtcConfig: RTC_CONFIG,
};

/** Joiner gets 60s to connect; host waits indefinitely. */
const JOIN_TIMEOUT_MS = 60000;

export class Connection implements IP2PConnection {
  private room: Room | null = null;
  private state: ConnectionState = "disconnected";
  private peerIds = new Set<string>();
  private sendMsg: ((data: string, targetPeers?: string | string[] | null) => Promise<void[]>) | null = null;
  private events: ConnectionEvents;
  private code: string | null = null;
  private isHost = false;

  constructor(events: ConnectionEvents) {
    this.events = events;
  }

  getState(): ConnectionState {
    return this.state;
  }

  getPeerIds(): string[] {
    return [...this.peerIds];
  }

  getPeerId(): string | null {
    return this.peerIds.size > 0 ? this.peerIds.values().next().value as string : null;
  }

  getCode(): string | null {
    return this.code;
  }

  /**
   * Join a room.
   * @param code - Room code to join
   * @param isHost - If true, waits indefinitely for a peer. If false, times out after 60s.
   */
  async join(code: string, isHost = false): Promise<void> {
    this.code = code;
    this.isHost = isHost;
    this.setState("connecting");
    console.log(`[Connection] ${isHost ? "hosting" : "joining"} room ${code} via trackers: ${TRYK_CONFIG.relayUrls.join(", ")}`);
    const roomId = codeToRoomId(code);

    try {
      this.room = joinRoom(TRYK_CONFIG, roomId);
      const [send, receive] = this.room.makeAction<string>("msg");
      this.sendMsg = send;

      receive((data: string, peerId: string) => {
        try {
          const msg = JSON.parse(data) as P2PMessage;
          console.log(`[Connection] received ${msg.type} from ${peerId}`);
          this.events.onMessage(msg, peerId);
        } catch (err) {
          console.warn(`[Connection] invalid message from ${peerId}:`, err);
        }
      });

      this.room.onPeerJoin((peerId: string) => {
        console.log(`[Connection] peer joined: ${peerId}`);
        const isFirst = this.peerIds.size === 0;
        this.peerIds.add(peerId);
        if (isFirst) {
          this.setState("connected");
          this.events.onPeerJoin(peerId);
        } else if (this.peerIds.has(peerId)) {
          this.setState("connected");
          this.events.onPeerReconnect?.(peerId);
        }
      });

      this.room.onPeerLeave((peerId: string) => {
        console.log(`[Connection] peer left: ${peerId}`);
        this.peerIds.delete(peerId);
        if (this.peerIds.size === 0) {
          this.setState("reconnecting");
        }
        this.events.onPeerLeave(peerId);
      });

      // Host waits indefinitely; joiner times out after JOIN_TIMEOUT_MS
      if (isHost) {
        await this.waitForPeerJoin(0);
      } else {
        await this.waitForPeerJoin(JOIN_TIMEOUT_MS);
      }
    } catch (err) {
      console.error("[Connection] join failed:", err);
      this.setState("error");
      throw err;
    }
  }

  async reconnect(): Promise<void> {
    if (!this.code) throw new Error("No room code to reconnect to");
    await this.leave();
    await this.join(this.code, this.isHost);
  }

  async send(msg: P2PMessage): Promise<void> {
    if (!this.sendMsg || this.peerIds.size === 0) throw new Error("Not connected to a peer");
    console.log(`[Connection] sending ${msg.type} to ${this.getPeerId()}`);
    await this.sendMsg(JSON.stringify(msg), this.getPeerId());
  }

  async broadcast(msg: P2PMessage): Promise<void> {
    if (!this.sendMsg) throw new Error("Not connected");
    console.log(`[Connection] broadcasting ${msg.type}`);
    await this.sendMsg(JSON.stringify(msg), null);
  }

  async leave(): Promise<void> {
    console.log("[Connection] leaving room");
    if (this.room) {
      await this.room.leave();
      this.room = null;
    }
    this.peerIds.clear();
    this.sendMsg = null;
    this.setState("disconnected");
  }

  /**
   * Wait for a peer to join.
   * If timeoutMs <= 0, waits forever (host mode).
   */
  private waitForPeerJoin(timeoutMs: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.peerIds.size > 0) {
        resolve();
        return;
      }
      let timer: ReturnType<typeof setTimeout> | null = null;
      if (timeoutMs > 0) {
        timer = setTimeout(() => {
          clearInterval(check);
          reject(new Error(`No peer joined within ${timeoutMs}ms`));
        }, timeoutMs);
      }
      const check = setInterval(() => {
        if (this.peerIds.size > 0) {
          if (timer) clearTimeout(timer);
          clearInterval(check);
          resolve();
        }
      }, 200);
    });
  }

  private setState(state: ConnectionState): void {
    console.log(`[Connection] state -> ${state}`);
    this.state = state;
    this.events.onStateChange(state);
  }
}